/**
 * 목록 필터·페이지를 URL search params와 동기화하는 훅.
 * 검색 조건의 SSOT는 URL 쿼리이며, 상세 복귀(뒤로가기) 시 히스토리 쿼리로 유지된다.
 */
import {
  mergeListSearch,
  toUrlSearch,
  type ListSearchValues,
} from '@/shared/lib/listSearchParams';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useRef } from 'react';

type UseListSearchParamsOptions = {
  /** replace navigate (필터 변경 시 히스토리 쌓지 않음) */
  replace?: boolean;
};

export const useListSearchParams = <T extends ListSearchValues>(
  defaults: T,
  { replace = true }: UseListSearchParamsOptions = {},
) => {
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const values = useMemo(
    () => mergeListSearch(defaultsRef.current, rawSearch),
    [rawSearch],
  );

  const setValues = useCallback(
    (patch: Partial<T> | ((prev: T) => T)) => {
      const prev = mergeListSearch(defaultsRef.current, rawSearch);
      const next =
        typeof patch === 'function'
          ? patch(prev)
          : mergeListSearch(defaultsRef.current, { ...prev, ...patch });
      navigate({
        search: toUrlSearch(next, defaultsRef.current),
        replace,
      } as never);
    },
    [navigate, rawSearch, replace],
  );

  return { values, setValues };
};
